import {
  useCreateReportFilterPreset,
  useDeleteReportFilterPreset,
} from "@/lib/data/react-query/reports/reports-report-filter-presets";
import { retrieveReportFilterPresetsQueryOptions } from "@/lib/data/react-query";
import { type ReportFilterPreset } from "@/lib/types";
import {
  buildPresetColumnOrder,
  buildPresetColumnVisibility,
  buildPresetColumnsFromVisibility,
  getOrderedTableColumnIds,
  getStoredTableState,
  getPresetFilterOptions,
  getPresetOptionValue,
  normalizePresetStringArray,
  updateStoredTableColumns,
} from "@/lib/utils/report-filter-presets";
import { useQuery } from "@tanstack/react-query";
import { type VisibilityState } from "@tanstack/react-table";
import { isEqual } from "lodash";
import { useEffect, useMemo, useState } from "react";
import toast from "react-hot-toast";

type UseReportFilterPresetsArgs<TSearch> = {
  availableColumnIds: string[];
  buildSearchFromPreset: (filterOptions: Record<string, unknown>) => TSearch;
  currentFilterOptions: Record<string, unknown>;
  defaultColumnVisibility?: VisibilityState;
  enabled: boolean;
  lockedVisibleColumnIds?: string[];
  onApplySearch: (search: TSearch) => Promise<void>;
  presetKeyByColumnId: Record<string, string>;
  reportType: string;
  tableName: string;
};

type AppliedPresetSnapshot = {
  columns: unknown;
  filters: unknown;
  isPendingMatch: boolean;
  value: string;
};

const normalizeComparisonValue = (value: unknown): unknown => {
  if (Array.isArray(value)) {
    return [...value]
      .map((item) => normalizeComparisonValue(item))
      .sort((leftItem, rightItem) => {
        const leftValue = JSON.stringify(leftItem);
        const rightValue = JSON.stringify(rightItem);
        return leftValue.localeCompare(rightValue);
      });
  }
  if (value && typeof value === "object") {
    const entries = Object.entries(value as Record<string, unknown>).map(
      ([key, item]) => [key, normalizeComparisonValue(item)] as const,
    );
    entries.sort(([leftKey], [rightKey]) => leftKey.localeCompare(rightKey));
    return Object.fromEntries(entries);
  }
  return value;
};

export function useReportFilterPresets<TSearch>({
  availableColumnIds,
  buildSearchFromPreset,
  currentFilterOptions,
  defaultColumnVisibility = {},
  enabled,
  lockedVisibleColumnIds,
  onApplySearch,
  presetKeyByColumnId,
  reportType,
  tableName,
}: UseReportFilterPresetsArgs<TSearch>) {
  const [presetSelectValue, setPresetSelectValue] = useState<
    string | undefined
  >();
  const [isSavePresetOpen, setIsSavePresetOpen] = useState(false);
  const [presetDescription, setPresetDescription] = useState("");
  const [presetName, setPresetName] = useState("");
  const [tableRenderKey, setTableRenderKey] = useState(0);
  const [columnStateVersion, setColumnStateVersion] = useState(0);
  const [appliedPresetSnapshot, setAppliedPresetSnapshot] = useState<
    AppliedPresetSnapshot | undefined
  >();

  const {
    data: reportFilterPresetsData,
    isLoading: isReportFilterPresetsLoading,
  } = useQuery({
    ...retrieveReportFilterPresetsQueryOptions({
      reportFilterPresetReportType: reportType,
    }),
    enabled,
  });
  const createReportFilterPresetMutation = useCreateReportFilterPreset();
  const deleteReportFilterPresetMutation = useDeleteReportFilterPreset();

  const reportFilterPresets = useMemo(() => {
    const presets = reportFilterPresetsData?.data ?? [];
    return [...presets].sort((a, b) =>
      (a.reportFilterPresetName ?? "").localeCompare(
        b.reportFilterPresetName ?? "",
      ),
    );
  }, [reportFilterPresetsData?.data]);

  const presetOptions = useMemo(
    () =>
      reportFilterPresets.map((preset, index) => ({
        description: preset.reportFilterPresetDescription?.trim() || undefined,
        label: preset.reportFilterPresetName ?? "Untitled preset",
        value: getPresetOptionValue(preset, index),
      })),
    [reportFilterPresets],
  );
  const currentComparableFilters = useMemo(
    () => normalizeComparisonValue(currentFilterOptions),
    [currentFilterOptions],
  );
  const currentComparableColumns = useMemo(() => {
    void columnStateVersion;
    return buildPresetColumnsFromVisibility({
      availableColumnIds,
      defaultColumnVisibility,
      presetKeyByColumnId,
      tableName,
    });
  }, [
    availableColumnIds,
    columnStateVersion,
    defaultColumnVisibility,
    presetKeyByColumnId,
    tableName,
  ]);
  const savePreviewColumns = useMemo(() => {
    void columnStateVersion;

    const columnVisibility = {
      ...defaultColumnVisibility,
      ...(getStoredTableState(tableName)?.columnVisibility ?? {}),
    };
    const orderedColumnIds = getOrderedTableColumnIds({
      availableColumnIds,
      tableName,
    });

    return orderedColumnIds.filter(
      (columnId) => columnVisibility[columnId] ?? true,
    );
  }, [
    availableColumnIds,
    columnStateVersion,
    defaultColumnVisibility,
    tableName,
  ]);

  const applyPreset = async (preset: ReportFilterPreset) => {
    const nextPresetValue = getPresetOptionValue(
      preset,
      reportFilterPresets.findIndex(
        (item) => item.reportFilterPresetId === preset.reportFilterPresetId,
      ),
    );
    const nextComparableFilters = normalizeComparisonValue(
      getPresetFilterOptions(preset),
    );
    const nextColumnVisibility = buildPresetColumnVisibility({
      availableColumnIds,
      lockedVisibleColumnIds,
      presetColumns: preset.reportFilterPresetColumns,
      presetKeyByColumnId,
    });
    const nextComparableColumns = normalizePresetStringArray(
      preset.reportFilterPresetColumns,
    );
    const nextColumnOrder = buildPresetColumnOrder({
      availableColumnIds,
      presetColumns: preset.reportFilterPresetColumns,
      presetKeyByColumnId,
      tableName,
    });

    updateStoredTableColumns({
      columnOrder: nextColumnOrder,
      columnVisibility: nextColumnVisibility,
      tableName,
    });
    setColumnStateVersion((prev) => prev + 1);
    setPresetSelectValue(nextPresetValue);
    setAppliedPresetSnapshot({
      columns: nextComparableColumns,
      filters: nextComparableFilters,
      isPendingMatch: true,
      value: nextPresetValue,
    });
    await onApplySearch(buildSearchFromPreset(getPresetFilterOptions(preset)));
    setTableRenderKey((prev) => prev + 1);
  };

  const applyPresetByValue = async (value: string) => {
    const selectedPreset = reportFilterPresets.find(
      (preset, index) => getPresetOptionValue(preset, index) === value,
    );
    if (!selectedPreset) return;
    await applyPreset(selectedPreset);
  };

  const savePreset = async () => {
    const trimmedPresetName = presetName.trim();
    if (!trimmedPresetName) {
      toast.error("Enter a preset name before saving.");
      return;
    }

    const response = await createReportFilterPresetMutation.mutateAsync({
      reportFilterPresetColumns: buildPresetColumnsFromVisibility({
        availableColumnIds,
        defaultColumnVisibility,
        presetKeyByColumnId,
        tableName,
      }),
      reportFilterPresetDescription: presetDescription.trim() || undefined,
      reportFilterPresetFilterOptions: currentFilterOptions,
      reportFilterPresetName: trimmedPresetName,
      reportFilterPresetReportType: reportType,
    });

    setIsSavePresetOpen(false);
    setPresetDescription("");
    setPresetName("");

    const createdPreset = response.data;
    const presetIndex = reportFilterPresets.findIndex(
      (preset) =>
        preset.reportFilterPresetId === createdPreset.reportFilterPresetId,
    );
    const nextPresetValue = getPresetOptionValue(createdPreset, presetIndex);
    setPresetSelectValue(nextPresetValue);
    setAppliedPresetSnapshot({
      columns: currentComparableColumns,
      filters: currentComparableFilters,
      isPendingMatch: false,
      value: nextPresetValue,
    });
  };

  const clearSelectedPreset = () => {
    setPresetSelectValue(undefined);
    setAppliedPresetSnapshot(undefined);
  };

  const deletePresetByValue = async (value: string) => {
    const selectedPreset = reportFilterPresets.find(
      (preset, index) => getPresetOptionValue(preset, index) === value,
    );

    if (!selectedPreset?.reportFilterPresetId) {
      toast.error("Unable to delete this preset.");
      return;
    }

    await deleteReportFilterPresetMutation.mutateAsync({
      reportFilterPresetId: selectedPreset.reportFilterPresetId,
    });

    if (presetSelectValue === value) {
      clearSelectedPreset();
    }
  };

  useEffect(() => {
    if (!appliedPresetSnapshot) {
      return;
    }

    const isMatchingFilters = isEqual(
      appliedPresetSnapshot.filters,
      currentComparableFilters,
    );
    const isMatchingColumns = isEqual(
      appliedPresetSnapshot.columns,
      currentComparableColumns,
    );

    if (isMatchingFilters && isMatchingColumns) {
      if (appliedPresetSnapshot.isPendingMatch) {
        setAppliedPresetSnapshot((previous) =>
          previous
            ? {
                ...previous,
                isPendingMatch: false,
              }
            : previous,
        );
      }
      return;
    }

    if (!appliedPresetSnapshot.isPendingMatch) {
      clearSelectedPreset();
    }
  }, [
    appliedPresetSnapshot,
    columnStateVersion,
    currentComparableColumns,
    currentComparableFilters,
  ]);

  const handleColumnStateChange = () => {
    setColumnStateVersion((prev) => prev + 1);
  };

  return {
    handleColumnOrderChange: handleColumnStateChange,
    handleColumnVisibilityChange: handleColumnStateChange,
    clearSelectedPreset,
    createReportFilterPresetMutation,
    deletePresetByValue,
    deleteReportFilterPresetMutation,
    isReportFilterPresetsLoading,
    isSavePresetOpen,
    presetDescription,
    presetName,
    presetOptions,
    presetSelectValue,
    reportFilterPresets,
    savePreset,
    savePreviewColumns,
    setIsSavePresetOpen: (open: boolean) => {
      setIsSavePresetOpen(open);
      if (!open) {
        setPresetDescription("");
        setPresetName("");
      }
    },
    setPresetDescription,
    setPresetName,
    setPresetSelectValue,
    tableRenderKey,
    applyPresetByValue,
  };
}
